export interface TrafficSourceInfo {
  referrer: string | null;
  referrer_domain: string | null;
  user_type: "new" | "returning";
  session_id: string;
  user_id: string | null;
  region: string | null;
  device_type: string;
  browser: string;
  os: string;
}

const ORGANIC_SOURCES = [
  "google",
  "bing",
  "yahoo",
  "duckduckgo",
  "baidu",
  "yandex",
  "ecosia",
  "startpage",
  "brave",
  "qwant",
  "seznam",
  "naver",
  "daum",
];

const PAID_SOURCES = ["google", "bing", "facebook", "instagram", "linkedin", "twitter", "x"];

const SOCIAL_SOURCES = [
  "facebook",
  "fb",
  "instagram",
  "linkedin",
  "twitter",
  "x",
  "youtube",
  "reddit",
  "pinterest",
  "tiktok",
  "snapchat",
];

const EMAIL_SOURCES = ["mail", "email", "newsletter", "mailchimp", "sendgrid", "hubspot"];

function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
}

function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("Chrome/") && !ua.includes("Chromium")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/") && !ua.includes("Chrome/")) return "Safari";
  if (ua.includes("MSIE") || ua.includes("Trident/")) return "IE";
  return "Unknown";
}

function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac OS") || ua.includes("macOS")) return "macOS";
  if (ua.includes("Linux") && !ua.includes("Android")) return "Linux";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  return "Unknown";
}

function isOrganicSearch(referrer: string | null, utmSource: string | null, utmMedium: string | null): boolean {
  if (utmMedium && utmMedium.toLowerCase() === "organic") return true;
  if (utmSource && utmMedium && utmMedium.toLowerCase() !== "cpc" && utmMedium.toLowerCase() !== "paid") {
    const source = utmSource.toLowerCase();
    if (ORGANIC_SOURCES.some((s) => source.includes(s))) return true;
  }
  if (referrer) {
    const host = getHostname(referrer);
    if (host !== null && ORGANIC_SOURCES.some((s) => host.includes(s))) return true;
  }
  return false;
}

function classifyTrafficSource(
  referrer: string | null,
  utmSource: string | null,
  utmMedium: string | null,
  utmCampaign: string | null
): { source: string; medium: string } {
  const source = utmSource?.toLowerCase() || "direct";
  const medium = utmMedium?.toLowerCase() || "none";

  if (medium === "organic" || isOrganicSearch(referrer, utmSource, utmMedium)) {
    return { source: "organic", medium: "organic" };
  }

  if (medium === "cpc" || medium === "ppc" || medium === "paid") {
    return { source: source || "paid", medium: "cpc" };
  }

  if (medium === "referral" || (referrer && !isOrganicSearch(referrer, utmSource, utmMedium))) {
    const host = getHostname(referrer || "");
    if (host !== null && SOCIAL_SOURCES.some((s) => host.includes(s))) {
      return { source: "social", medium: "social" };
    }
    return { source: "referral", medium: "referral" };
  }

  if (medium === "email" || (utmSource && EMAIL_SOURCES.some((s) => source.includes(s)))) {
    return { source: "email", medium: "email" };
  }

  if (medium === "social" || (utmSource && SOCIAL_SOURCES.some((s) => source.includes(s)))) {
    return { source: "social", medium: "social" };
  }

  if (medium === "none" && source === "direct") {
    return { source: "direct", medium: "none" };
  }

  return { source, medium };
}

function getPageType(pathname: string): string {
  if (pathname.includes("/blog") || pathname.includes("/article")) return "blog";
  if (pathname.includes("/product") || pathname.includes("/pdp") || pathname.includes("/fund/") || pathname.includes("/stock/")) return "product/PDP";
  if (pathname.includes("/calculator") || pathname.includes("/tool")) return "calculator";
  if (pathname === "/" || pathname.includes("/landing")) return "landing";
  return "other";
}

function getRegion(): string | null {
  return null;
}

export function getTrafficSourceInfo(pathname: string): TrafficSourceInfo {
  const referrer = document.referrer || null;
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source");
  const utmMedium = urlParams.get("utm_medium");
  const utmCampaign = urlParams.get("utm_campaign");
  const utmContent = urlParams.get("utm_content");
  const utmTerm = urlParams.get("utm_term");

  const { source, medium } = classifyTrafficSource(referrer, utmSource, utmMedium, utmCampaign);

  const sessionCountStr = sessionStorage.getItem("mp_session_count");
  let sessionNumber = 1;
  if (sessionCountStr) {
    sessionNumber = parseInt(sessionCountStr, 10) + 1;
  }
  sessionStorage.setItem("mp_session_count", String(sessionNumber));
  const sessionId = `sess_${Date.now()}_${sessionNumber}`;

  const isNewUser = !sessionStorage.getItem("mp_returning_user");
  sessionStorage.setItem("mp_returning_user", "true");
  const userType = isNewUser ? "new" : "returning";

  const uniqueId = localStorage.getItem("uniqueId") || localStorage.getItem("user_id");
  const userId = uniqueId || null;

  const region = getRegion();
  const referrerDomain = referrer ? getHostname(referrer) : null;

  return {
    referrer,
    referrer_domain: referrerDomain,
    user_type: userType,
    session_id: sessionId,
    user_id: userId,
    region,
    device_type: getDeviceType(),
    browser: getBrowser(),
    os: getOS(),
  };
}

export function getPageTypeForExport(pathname: string): string {
  return getPageType(pathname);
}

export function getQueryParams(query: string): Record<string, string> {
  const parsedParams: Record<string, string> = {};
  try {
    if (query && query.length > 0) {
      const urlParams = new URLSearchParams(query);
      urlParams.forEach((value, key) => {
        parsedParams[key] = value;
      });
    }
  } catch (error) {
    console.error("Error parsing query params:", error);
  }
  return parsedParams;
}